export interface LoomUnionSharedSeamReceipt {
  seamId: string;
  sourceNodeIndices: [number, number];
  patchIndices: [number, number];
  vertexIndicesArray: string;
  parametersAArray: string;
  parametersBArray: string;
  maxSurfaceGapM: number;
  maxImplicitResidual: number;
  closed: true;
}

export interface LoomUnionPairReceipt {
  sourceNodeIndices: [number, number];
  patchPairCount: number;
  loopCount: number;
  seedEdgeCount: number;
  maxSurfaceGapM: number;
  maxImplicitResidual: number;
}

export interface LoomUnionQcReceipt {
  vertexCount: number;
  faceCount: number;
  boundaryEdgeCount: number;
  nonmanifoldEdgeCount: number;
  windingConsistent: true;
  signedVolume: number;
  positiveVolume: true;
  seamEdgeCount: number;
  seamZeroGap: true;
  selfIntersectionCount: number;
  eulerCharacteristic: number;
  componentCount: number;
  canonicalQcPassed: true;
}

export interface LoomAcceptedUnionReceipt {
  algorithmId: "loom.implicit-guided-explicit-union/v1";
  evaluatorId: "loom.native-wing-body/v3";
  topologyId: "selected-wing-fuselage-pairwise-union";
  definitionRevision: string;
  surfaceGeometryRevision: string;
  surfaceTopologyRevision: string;
  kernelTopologyRevision: string;
  indexSpace: "surface";
  faceSourceNodeIndicesArray: string;
  facePatchIndicesArray: string;
  faceCornerParametersArray: string;
  sharedSeams: LoomUnionSharedSeamReceipt[];
  pairReceipts: LoomUnionPairReceipt[];
  qc: LoomUnionQcReceipt;
}

interface UnionReceiptContext {
  arrays: Record<string, unknown>;
  surfaceVertexCount: number;
  surfaceFaceCount: number;
  sourceNodeCount: number;
  patchCount: number;
  definitionRevision: string;
}

interface UnionArrayDescriptor {
  dtype: "uint32" | "float32";
  shape: number[];
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function exact(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  const result = record(value, label); const actual = Object.keys(result);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
  return result;
}

function integer(value: unknown, label: string, maximum = Number.MAX_SAFE_INTEGER): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)
      || value < 0 || value >= maximum) throw new Error(`${label} is out of bounds`);
  return value;
}

function signedInteger(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)
      || value < -1_000_000 || value > 1_000_000) {
    throw new Error(`${label} is out of bounds`);
  }
  return value;
}

function finite(value: unknown, label: string, positive = false): number {
  if (typeof value !== "number" || !Number.isFinite(value)
      || (positive ? value <= 0 : value < 0)) throw new Error(`${label} is invalid`);
  return value;
}

function revision(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^sha256:[0-9a-f]{64}$/.test(value)) {
    throw new Error(`${label} is not a SHA-256 revision`);
  }
  return value;
}

function pair(
  value: unknown, label: string, maximum: number,
): [number, number] {
  if (!Array.isArray(value) || value.length !== 2) throw new Error(`${label} is malformed`);
  const result: [number, number] = [
    integer(value[0], `${label}[0]`, maximum),
    integer(value[1], `${label}[1]`, maximum),
  ];
  if (result[0] === result[1]) throw new Error(`${label} must identify two values`);
  return result;
}

function descriptor(
  context: UnionReceiptContext, value: unknown, dtype: "uint32" | "float32",
  shape: readonly number[], label: string,
): string {
  if (typeof value !== "string" || !value) throw new Error(`${label} is invalid`);
  const raw = context.arrays[value] as UnionArrayDescriptor | undefined;
  if (!raw || raw.dtype !== dtype || !Array.isArray(raw.shape)
      || raw.shape.length !== shape.length
      || raw.shape.some((item, index) => item !== shape[index])) {
    throw new Error(`${label} does not bind its declared payload array`);
  }
  return value;
}

function parseSharedSeam(
  value: unknown, index: number, context: UnionReceiptContext,
): LoomUnionSharedSeamReceipt {
  const label = `acceptedGeometry.unionReceipt.sharedSeams[${index}]`;
  const raw = exact(value, ["seamId", "sourceNodeIndices", "patchIndices",
    "vertexIndicesArray", "parametersAArray", "parametersBArray",
    "maxSurfaceGapM", "maxImplicitResidual", "closed"], label);
  if (typeof raw.seamId !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/.test(raw.seamId)
      || raw.closed !== true) throw new Error(`${label} identity is invalid`);
  const verticesName = typeof raw.vertexIndicesArray === "string" ? raw.vertexIndicesArray : "";
  const vertices = context.arrays[verticesName] as UnionArrayDescriptor | undefined;
  if (!vertices || vertices.dtype !== "uint32" || !Array.isArray(vertices.shape)
      || vertices.shape.length !== 1
      || vertices.shape[0] === undefined || vertices.shape[0] < 3
      || vertices.shape[0] > 4_096) throw new Error(`${label} vertex array is invalid`);
  const count = vertices.shape[0];
  return {
    seamId: raw.seamId,
    sourceNodeIndices: pair(raw.sourceNodeIndices, `${label}.sourceNodeIndices`, context.sourceNodeCount),
    patchIndices: pair(raw.patchIndices, `${label}.patchIndices`, context.patchCount),
    vertexIndicesArray: verticesName,
    parametersAArray: descriptor(context, raw.parametersAArray, "float32", [count, 2], `${label}.parametersAArray`),
    parametersBArray: descriptor(context, raw.parametersBArray, "float32", [count, 2], `${label}.parametersBArray`),
    maxSurfaceGapM: finite(raw.maxSurfaceGapM, `${label}.maxSurfaceGapM`),
    maxImplicitResidual: finite(raw.maxImplicitResidual, `${label}.maxImplicitResidual`),
    closed: true,
  };
}

function parsePairReceipt(
  value: unknown, index: number, context: UnionReceiptContext,
): LoomUnionPairReceipt {
  const label = `acceptedGeometry.unionReceipt.pairReceipts[${index}]`;
  const raw = exact(value, ["sourceNodeIndices", "patchPairCount", "loopCount",
    "seedEdgeCount", "maxSurfaceGapM", "maxImplicitResidual"], label);
  return {
    sourceNodeIndices: pair(raw.sourceNodeIndices, `${label}.sourceNodeIndices`, context.sourceNodeCount),
    patchPairCount: integer(raw.patchPairCount, `${label}.patchPairCount`),
    loopCount: integer(raw.loopCount, `${label}.loopCount`),
    seedEdgeCount: integer(raw.seedEdgeCount, `${label}.seedEdgeCount`),
    maxSurfaceGapM: finite(raw.maxSurfaceGapM, `${label}.maxSurfaceGapM`),
    maxImplicitResidual: finite(raw.maxImplicitResidual, `${label}.maxImplicitResidual`),
  };
}

function parseQc(value: unknown, context: UnionReceiptContext): LoomUnionQcReceipt {
  const label = "acceptedGeometry.unionReceipt.qc";
  const raw = exact(value, ["vertexCount", "faceCount", "boundaryEdgeCount",
    "nonmanifoldEdgeCount", "windingConsistent", "signedVolume", "positiveVolume",
    "seamEdgeCount", "seamZeroGap", "selfIntersectionCount",
    "eulerCharacteristic", "componentCount", "canonicalQcPassed"], label);
  const result = {
    vertexCount: integer(raw.vertexCount, `${label}.vertexCount`),
    faceCount: integer(raw.faceCount, `${label}.faceCount`),
    boundaryEdgeCount: integer(raw.boundaryEdgeCount, `${label}.boundaryEdgeCount`),
    nonmanifoldEdgeCount: integer(raw.nonmanifoldEdgeCount, `${label}.nonmanifoldEdgeCount`),
    windingConsistent: raw.windingConsistent,
    signedVolume: finite(raw.signedVolume, `${label}.signedVolume`, true),
    positiveVolume: raw.positiveVolume,
    seamEdgeCount: integer(raw.seamEdgeCount, `${label}.seamEdgeCount`),
    seamZeroGap: raw.seamZeroGap,
    selfIntersectionCount: integer(raw.selfIntersectionCount, `${label}.selfIntersectionCount`),
    eulerCharacteristic: signedInteger(raw.eulerCharacteristic, `${label}.eulerCharacteristic`),
    componentCount: integer(raw.componentCount, `${label}.componentCount`),
    canonicalQcPassed: raw.canonicalQcPassed,
  };
  if (result.vertexCount !== context.surfaceVertexCount
      || result.faceCount !== context.surfaceFaceCount
      || result.boundaryEdgeCount !== 0 || result.nonmanifoldEdgeCount !== 0
      || result.windingConsistent !== true || result.positiveVolume !== true
      || result.seamZeroGap !== true || result.selfIntersectionCount !== 0
      || result.eulerCharacteristic > 2 || result.eulerCharacteristic % 2 !== 0
      || result.componentCount !== 1 || result.canonicalQcPassed !== true) {
    throw new Error(`${label} did not prove an accepted exact union`);
  }
  return result as LoomUnionQcReceipt;
}

export function parseLoomAcceptedUnionReceipt(
  value: unknown, context: UnionReceiptContext,
): LoomAcceptedUnionReceipt {
  const raw = exact(value, ["algorithmId", "evaluatorId", "topologyId",
    "definitionRevision", "surfaceGeometryRevision", "surfaceTopologyRevision",
    "kernelTopologyRevision",
    "indexSpace", "faceSourceNodeIndicesArray", "facePatchIndicesArray",
    "faceCornerParametersArray", "sharedSeams", "pairReceipts", "qc"],
  "acceptedGeometry.unionReceipt");
  if (raw.algorithmId !== "loom.implicit-guided-explicit-union/v1"
      || raw.evaluatorId !== "loom.native-wing-body/v3"
      || raw.topologyId !== "selected-wing-fuselage-pairwise-union"
      || raw.indexSpace !== "surface") throw new Error("v3 union receipt discriminator is unsupported");
  if (!Array.isArray(raw.sharedSeams) || raw.sharedSeams.length < 1
      || raw.sharedSeams.length > 16 || !Array.isArray(raw.pairReceipts)
      || raw.pairReceipts.length < 1 || raw.pairReceipts.length > 120) {
    throw new Error("v3 union receipt exceeds its receipt budget");
  }
  const definitionRevision = revision(raw.definitionRevision, "union definition revision");
  if (definitionRevision !== context.definitionRevision) {
    throw new Error("v3 union receipt is not definition-bound");
  }
  const result: LoomAcceptedUnionReceipt = {
    algorithmId: "loom.implicit-guided-explicit-union/v1",
    evaluatorId: "loom.native-wing-body/v3",
    topologyId: "selected-wing-fuselage-pairwise-union",
    definitionRevision,
    surfaceGeometryRevision: revision(raw.surfaceGeometryRevision, "union surface geometry revision"),
    surfaceTopologyRevision: revision(raw.surfaceTopologyRevision, "union surface topology revision"),
    kernelTopologyRevision: revision(raw.kernelTopologyRevision, "union kernel topology revision"),
    indexSpace: "surface",
    faceSourceNodeIndicesArray: descriptor(context, raw.faceSourceNodeIndicesArray, "uint32", [context.surfaceFaceCount], "union face sources"),
    facePatchIndicesArray: descriptor(context, raw.facePatchIndicesArray, "uint32", [context.surfaceFaceCount], "union face patches"),
    faceCornerParametersArray: descriptor(context, raw.faceCornerParametersArray, "float32", [context.surfaceFaceCount, 3, 2], "union face parameters"),
    sharedSeams: raw.sharedSeams.map((item, index) => parseSharedSeam(item, index, context)),
    pairReceipts: raw.pairReceipts.map((item, index) => parsePairReceipt(item, index, context)),
    qc: parseQc(raw.qc, context),
  };
  const pairIdentities = result.pairReceipts.map((item) => (
    [...item.sourceNodeIndices].sort((left, right) => left - right).join(":")));
  const arrayNames = [result.faceSourceNodeIndicesArray, result.facePatchIndicesArray,
    result.faceCornerParametersArray, ...result.sharedSeams.flatMap((item) => (
      [item.vertexIndicesArray, item.parametersAArray, item.parametersBArray]))];
  const seamIds = result.sharedSeams.map((item) => item.seamId);
  if (new Set(result.sharedSeams.map((item) => item.seamId)).size !== result.sharedSeams.length
      || seamIds.some((item, index) => index > 0 && item <= seamIds[index - 1]!)
      || new Set(pairIdentities).size !== pairIdentities.length
      || new Set(arrayNames).size !== arrayNames.length
      || result.qc.seamEdgeCount !== result.sharedSeams.reduce((sum, item) => (
        sum + ((context.arrays[item.vertexIndicesArray] as UnionArrayDescriptor).shape[0] ?? 0)
      ), 0)) throw new Error("v3 union seam receipt is inconsistent");
  return result;
}
